import { Client, GetOptions } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { getNextPageParam } from "../utils/page-params";

const databaseService = serviceHost.getDatabaseService();

const clientRepository = repositoryHost.getClientRepository(databaseService);
type CreateClientParams = Parameters<typeof clientRepository.create>[0];
type UpdateClientParams = Parameters<typeof clientRepository.update>[0];
type DeleteClientParams = Parameters<typeof clientRepository.delete>[0];

const defaultClientsOrderBy = {
  field: "updatedAt" as const,
  direction: "desc" as const,
};

export const useClients = (options: GetOptions<Client>) => {
  const mergedOptions: GetOptions<Client> = {
    ...options,
    orderBy: options.orderBy ?? defaultClientsOrderBy,
  };

  return useInfiniteQuery({
    queryKey: ["clients", "get", JSON.stringify(mergedOptions)],
    queryFn: ({ pageParam }) =>
      clientRepository.getAll({
        ...mergedOptions,
        pagination: {
          ...mergedOptions.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(mergedOptions),
  });
};

/**
 * Loads every client by eagerly paginating through all pages. Firestore has no
 * case-insensitive substring search, so client pickers filter locally — which
 * requires the full client set to be present. Use this for selection UIs (e.g.
 * creating a sales order) where any client must be findable, not just recent ones.
 */
export const useAllClients = () => {
  const query = useClients({ pagination: { limit: 200 } });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return query;
};

export const useClientById = (clientId: string) => {
  return useQuery({
    queryKey: ["clients", "get-by-id", clientId],
    enabled: clientId.length > 0,
    queryFn: () => clientRepository.get({ id: clientId }),
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const { hasClientVendorWriteAccess } = useUserStore();

  return useMutation<string, Error, CreateClientParams>({
    mutationKey: ["clients", "create"],
    mutationFn: async (params: CreateClientParams) => {
      if (!hasClientVendorWriteAccess()) {
        throw new Error("Access denied: You do not have permission to create clients");
      }

      return clientRepository.create(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  const { hasClientVendorWriteAccess } = useUserStore();

  return useMutation<void, Error, UpdateClientParams>({
    mutationKey: ["clients", "update"],
    mutationFn: async (params: UpdateClientParams) => {
      if (!hasClientVendorWriteAccess()) {
        throw new Error("Access denied: You do not have permission to update clients");
      }

      return clientRepository.update(params);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({
        queryKey: ["clients", "get-by-id", variables.id],
      });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, DeleteClientParams>({
    mutationKey: ["clients", "delete"],
    mutationFn: async (params: DeleteClientParams) => {
      if (!hasFullAccess()) {
        throw new Error("Access denied: Full access required to delete clients");
      }

      return clientRepository.delete(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
};
